import axios from "axios";

// Helper: prefer sessionStorage tokens when this tab is an impersonation session.
// Falls back to localStorage for the regular admin/user session.
function tokenStorage() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage?.getItem("impersonating") === "1") {
      return window.sessionStorage;
    }
  } catch {}
  return typeof window !== "undefined" ? window.localStorage : null;
}

function getAccessToken() {
  try { return tokenStorage()?.getItem("access_token") || null; } catch { return null; }
}
function getRefreshToken() {
  try { return tokenStorage()?.getItem("refresh_token") || null; } catch { return null; }
}
function setAccessToken(v) {
  try { tokenStorage()?.setItem("access_token", v); } catch {}
}
function clearTokens() {
  try {
    const s = tokenStorage();
    s?.removeItem("access_token");
    s?.removeItem("refresh_token");
  } catch {}
}

const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

// Add Bearer token from the active storage to every request
API.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Anti-abuse: Send screen info for device fingerprinting
  try {
    config.headers["X-Screen-Info"] = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
  } catch {}
  return config;
});

// Handle 401 - try refresh using stored refresh token
API.interceptors.response.use(
  (res) => res,
  async (err) => {
    const url = err.config?.url || "";
    const skipRefresh = url.includes("/auth/me") || url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/register");
    if (err.response?.status === 401 && !err.config._retry && !skipRefresh) {
      err.config._retry = true;
      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const { data } = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
            {},
            { headers: { Authorization: `Bearer ${refreshToken}` } }
          );
          if (data.access_token) setAccessToken(data.access_token);
          return API(err.config);
        }
      } catch {
        clearTokens();
      }
    }
    return Promise.reject(err);
  }
);

export function formatError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default API;
